"use client";

import { use, useState } from "react";
import { format } from "date-fns";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ArrowLeft,
  Clock,
  CalendarDays,
  Send,
  MoreHorizontal,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  CheckCircle2,
  Edit3,
  Trash2,
  Copy,
  ExternalLink,
  User,
  Tag,
  Eye,
  BarChart3,
  TrendingUp,
  MousePointer,
  ArrowUpRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  mockPosts,
  mockClients,
  mockApprovals,
  platformIcons,
  statusConfig,
  approvalStatusConfig,
  type MockPost,
} from "@/lib/mock-data";

const mockComments = [
  {
    id: "c1",
    author: "Sarah Chen",
    initials: "SC",
    text: "Love the tone on this one. Maybe we can add a call to action at the end?",
    time: "2 hours ago",
  },
  {
    id: "c2",
    author: "Marcus Johnson",
    initials: "MJ",
    text: "Good point — I will update the copy with a CTA. Also thinking we should tag the product page.",
    time: "1 hour ago",
  },
  {
    id: "c3",
    author: "Alex Rivera",
    initials: "AR",
    text: "Looks great! Approved from my side.",
    time: "30 min ago",
  },
];

const mockVersions = [
  { version: 1, date: "Apr 22, 2026", author: "Marcus Johnson", note: "Initial draft" },
  { version: 2, date: "Apr 24, 2026", author: "Marcus Johnson", note: "Updated copy per client feedback" },
  { version: 3, date: "Apr 26, 2026", author: "Sarah Chen", note: "Final revisions and CTA added" },
];

export default function PostDetailPage({
  params,
}: {
  params: Promise<{ id: string }>;
}) {
  const { id } = use(params);
  const router = useRouter();

  const originalPost = mockPosts.find((p) => p.id === id);
  const [editOpen, setEditOpen] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editPlatform, setEditPlatform] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editScheduledDate, setEditScheduledDate] = useState("");
  const [editScheduledTime, setEditScheduledTime] = useState("");
  const [saved, setSaved] = useState(false);

  // Local post state so edits reflect on the page
  const [postOverrides, setPostOverrides] = useState<Partial<MockPost>>({});
  const post = originalPost ? { ...originalPost, ...postOverrides } : undefined;

  function openEditModal() {
    if (!post) return;
    setEditContent(post.content);
    setEditPlatform(post.platform);
    setEditStatus(post.status);
    setEditScheduledDate(format(post.scheduledAt, "yyyy-MM-dd"));
    setEditScheduledTime(format(post.scheduledAt, "HH:mm"));
    setSaved(false);
    setEditOpen(true);
  }

  function handleSave() {
    if (!post) return;
    const newDate = new Date(`${editScheduledDate}T${editScheduledTime}`);
    setPostOverrides({
      content: editContent,
      platform: editPlatform,
      status: editStatus as MockPost["status"],
      scheduledAt: newDate,
    });
    setSaved(true);
    setTimeout(() => setEditOpen(false), 600);
  }

  if (!originalPost || !post) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-lg font-semibold mb-2">Post not found</p>
        <p className="text-sm text-muted-foreground mb-4">
          The post you are looking for does not exist.
        </p>
        <Link href="/content/posts">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Posts
          </Button>
        </Link>
      </div>
    );
  }

  const platform = platformIcons[post.platform];
  const status = statusConfig[post.status];
  const client = mockClients.find((c) => c.id === post.clientId);
  const approval = mockApprovals.find((a) => a.postId === post.id);
  const approvalStatus = approval
    ? approvalStatusConfig[approval.status]
    : null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b bg-card">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => router.back()}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold tracking-tight">Post Detail</h2>
              <span
                className={cn(
                  "text-[11px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1",
                  status.bg,
                  status.color
                )}
              >
                <span className={cn("h-1.5 w-1.5 rounded-full", status.dot)} />
                {status.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Created by {post.author} &middot;{" "}
              {format(post.scheduledAt, "MMM d, yyyy")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openEditModal}>
            <Edit3 className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
          {post.status === "DRAFT" && (
            <Button size="sm" className="shadow-sm shadow-primary/25">
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Submit for Approval
            </Button>
          )}
          {post.status === "APPROVED" && (
            <Button size="sm" className="shadow-sm shadow-primary/25">
              <CalendarDays className="h-3.5 w-3.5 mr-1.5" />
              Schedule
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger>
              <div className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted transition-colors">
                <MoreHorizontal className="h-4 w-4" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate Post
              </DropdownMenuItem>
              <DropdownMenuItem>
                <ExternalLink className="h-4 w-4 mr-2" />
                Copy Share Link
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Post
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
          {/* Left column — Post content + preview */}
          <div className="space-y-6">
            {/* Post Content */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Content</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border p-5 bg-muted/20">
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {post.content}
                  </p>
                </div>

                {/* Attached media */}
                {post.mediaUrl && (
                  <div className="mt-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Attached Media
                    </p>
                    <div className="rounded-lg overflow-hidden border max-w-md">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={post.mediaUrl}
                        alt=""
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Labels */}
                {post.labels.length > 0 && (
                  <div className="flex items-center gap-2 mt-4">
                    <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                    {post.labels.map((label) => (
                      <span
                        key={label.name}
                        className="text-[11px] font-medium px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: label.color }}
                      >
                        {label.name}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Platform Preview */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Platform Preview</CardTitle>
                  <span
                    className={cn(
                      "text-xs font-medium px-2 py-1 rounded-md",
                      platform?.bg,
                      platform?.color
                    )}
                  >
                    {platform?.label}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-w-sm mx-auto">
                  {post.platform === "INSTAGRAM" && (
                    <div className="rounded-lg border overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-[1.5px]">
                            <div className="h-full w-full rounded-full bg-card flex items-center justify-center">
                              <span className="text-[8px] font-bold">SS</span>
                            </div>
                          </div>
                          <span className="text-xs font-semibold">
                            socialstandards
                          </span>
                        </div>
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="aspect-square bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 flex items-center justify-center overflow-hidden">
                        {post.mediaUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={post.mediaUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <p className="text-xs text-muted-foreground">Image Preview</p>
                        )}
                      </div>
                      <div className="px-3 pt-2.5 pb-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-3">
                            <Heart className="h-5 w-5" />
                            <MessageCircle className="h-5 w-5" />
                            <Send className="h-5 w-5" />
                          </div>
                          <Bookmark className="h-5 w-5" />
                        </div>
                        <p className="text-xs leading-relaxed">
                          <span className="font-semibold mr-1">
                            socialstandards
                          </span>
                          {post.content}
                        </p>
                      </div>
                    </div>
                  )}

                  {post.platform === "TWITTER" && (
                    <div className="rounded-lg border p-3">
                      <div className="flex gap-2.5">
                        <Avatar className="h-9 w-9 shrink-0">
                          <AvatarFallback className="bg-slate-900 text-white text-[9px] font-bold">
                            SS
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-bold">
                              Social Standards
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              @socialstandards
                            </span>
                          </div>
                          <p className="text-[13px] mt-1 leading-relaxed">
                            {post.content}
                          </p>
                          <div className="flex items-center justify-between mt-3 max-w-[250px] text-muted-foreground">
                            <span className="flex items-center gap-1 text-[11px]">
                              <MessageCircle className="h-3.5 w-3.5" /> 12
                            </span>
                            <span className="flex items-center gap-1 text-[11px]">
                              <Share2 className="h-3.5 w-3.5" /> 8
                            </span>
                            <span className="flex items-center gap-1 text-[11px]">
                              <Heart className="h-3.5 w-3.5" /> 47
                            </span>
                            <Bookmark className="h-3.5 w-3.5" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {(post.platform === "LINKEDIN" ||
                    post.platform === "FACEBOOK") && (
                    <div className="rounded-lg border p-3">
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-blue-600 text-white text-xs font-bold">
                            SS
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-xs font-semibold">
                            Social Standards
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {format(post.scheduledAt, "MMM d")} &middot; 🌐
                          </p>
                        </div>
                      </div>
                      <p className="text-xs leading-relaxed whitespace-pre-line">
                        {post.content}
                      </p>
                      <Separator className="my-2.5" />
                      <div className="flex items-center justify-around text-muted-foreground">
                        <span className="flex items-center gap-1.5 text-[11px] font-medium">
                          <Heart className="h-3.5 w-3.5" /> Like
                        </span>
                        <span className="flex items-center gap-1.5 text-[11px] font-medium">
                          <MessageCircle className="h-3.5 w-3.5" /> Comment
                        </span>
                        <span className="flex items-center gap-1.5 text-[11px] font-medium">
                          <Share2 className="h-3.5 w-3.5" /> Share
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Analytics */}
            {post.engagement ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Performance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Key metrics */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="rounded-lg bg-muted/40 p-3 text-center">
                      <Eye className="h-4 w-4 text-blue-500 mx-auto mb-1.5" />
                      <p className="text-lg font-bold">{(post.engagement.reach / 1000).toFixed(1)}K</p>
                      <p className="text-[11px] text-muted-foreground">Reach</p>
                    </div>
                    <div className="rounded-lg bg-muted/40 p-3 text-center">
                      <Heart className="h-4 w-4 text-red-500 mx-auto mb-1.5" />
                      <p className="text-lg font-bold">{post.engagement.likes.toLocaleString()}</p>
                      <p className="text-[11px] text-muted-foreground">Likes</p>
                    </div>
                    <div className="rounded-lg bg-muted/40 p-3 text-center">
                      <MessageCircle className="h-4 w-4 text-violet-500 mx-auto mb-1.5" />
                      <p className="text-lg font-bold">{post.engagement.comments}</p>
                      <p className="text-[11px] text-muted-foreground">Comments</p>
                    </div>
                    <div className="rounded-lg bg-muted/40 p-3 text-center">
                      <Share2 className="h-4 w-4 text-emerald-500 mx-auto mb-1.5" />
                      <p className="text-lg font-bold">{post.engagement.shares}</p>
                      <p className="text-[11px] text-muted-foreground">Shares</p>
                    </div>
                  </div>

                  {/* Engagement rate bar */}
                  {(() => {
                    const total = post.engagement.likes + post.engagement.comments + post.engagement.shares;
                    const rate = ((total / post.engagement.reach) * 100).toFixed(1);
                    return (
                      <div className="rounded-lg border p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">Engagement Rate</span>
                          </div>
                          <span className="text-sm font-bold text-primary">{rate}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(parseFloat(rate) * 5, 100)}%` }} />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Engagement by hour */}
                  <div>
                    <p className="text-sm font-medium mb-3">Engagement Over Time</p>
                    <ResponsiveContainer width="100%" height={120}>
                      <AreaChart data={[
                        { h: "12a", v: 2 }, { h: "1a", v: 1 }, { h: "2a", v: 0 }, { h: "3a", v: 5 },
                        { h: "4a", v: 18 }, { h: "6a", v: 45 }, { h: "8a", v: 72 }, { h: "10a", v: 89 },
                        { h: "12p", v: 64 }, { h: "2p", v: 52 }, { h: "4p", v: 38 }, { h: "6p", v: 15 },
                        { h: "8p", v: 8 }, { h: "10p", v: 4 }, { h: "11p", v: 3 },
                      ]}>
                        <defs>
                          <linearGradient id="engGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--chart-primary)" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="var(--chart-primary)" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="h" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "var(--chart-text)" }} interval={2} />
                        <Tooltip
                          contentStyle={{ borderRadius: "8px", border: "1px solid var(--chart-grid)", fontSize: "12px", background: "#fff" }}
                          formatter={(value) => [`${value} interactions`, "Engagement"]}
                        />
                        <Area type="monotone" dataKey="v" stroke="var(--chart-primary)" strokeWidth={2} fill="url(#engGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Detailed breakdown */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Detailed Breakdown</p>
                    {[
                      { label: "Photo taps", value: 856, icon: MousePointer, pct: 100 },
                      { label: "Profile visits", value: 342, icon: User, pct: 40 },
                      { label: "Saves", value: 198, icon: Bookmark, pct: 23 },
                      { label: "Link clicks", value: 127, icon: ArrowUpRight, pct: 15 },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/30">
                        <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                          <item.icon className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium">{item.label}</span>
                            <span className="text-xs font-bold">{item.value.toLocaleString()}</span>
                          </div>
                          <div className="h-1 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${item.pct}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Audience */}
                  <div>
                    <p className="text-sm font-medium mb-3">Audience</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border p-3">
                        <p className="text-[11px] text-muted-foreground mb-2">Top Age Group</p>
                        <p className="text-lg font-bold">25-34</p>
                        <p className="text-xs text-muted-foreground">38% of audience</p>
                      </div>
                      <div className="rounded-lg border p-3">
                        <p className="text-[11px] text-muted-foreground mb-2">Top Location</p>
                        <p className="text-lg font-bold">New York</p>
                        <p className="text-xs text-muted-foreground">18% of audience</p>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {[
                        { group: "18-24", pct: 28 },
                        { group: "25-34", pct: 38 },
                        { group: "35-44", pct: 19 },
                        { group: "45-54", pct: 10 },
                        { group: "55+", pct: 5 },
                      ].map((d) => (
                        <div key={d.group} className="flex items-center gap-2">
                          <span className="text-[11px] w-10 shrink-0 text-muted-foreground">{d.group}</span>
                          <div className="flex-1 h-5 bg-muted rounded overflow-hidden">
                            <div className="h-full bg-primary/70 rounded flex items-center px-2" style={{ width: `${d.pct}%` }}>
                              {d.pct > 12 && <span className="text-[9px] font-semibold text-white">{d.pct}%</span>}
                            </div>
                          </div>
                          {d.pct <= 12 && <span className="text-[11px] font-medium w-8 text-right">{d.pct}%</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg bg-muted/30 p-6 text-center">
                    <BarChart3 className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm font-medium text-muted-foreground">No analytics yet</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Analytics will appear here once this post is published.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Comments / Internal Discussion */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  Team Discussion ({mockComments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {mockComments.map((comment) => (
                  <div
                    key={comment.id}
                    className="flex gap-3 p-3 rounded-lg bg-muted/30"
                  >
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarFallback className="text-[10px] bg-primary/10 text-primary font-semibold">
                        {comment.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">
                          {comment.author}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {comment.time}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed">{comment.text}</p>
                    </div>
                  </div>
                ))}

                <div className="pt-2">
                  <Textarea
                    placeholder="Add a comment..."
                    className="resize-none text-sm min-h-[80px]"
                  />
                  <div className="flex justify-end mt-2">
                    <Button size="sm" className="text-xs">
                      <Send className="h-3 w-3 mr-1.5" />
                      Comment
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column — Details sidebar */}
          <div className="space-y-6">
            {/* Details */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn("h-2 w-2 rounded-full", status.dot)}
                    />
                    <span
                      className={cn("text-sm font-medium", status.color)}
                    >
                      {status.label}
                    </span>
                  </div>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Platform
                  </span>
                  <span
                    className={cn(
                      "text-xs font-medium px-2 py-1 rounded-md",
                      platform?.bg,
                      platform?.color
                    )}
                  >
                    {platform?.label}
                  </span>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Author</span>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-[9px] bg-primary/10 text-primary font-semibold">
                        {post.authorInitials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{post.author}</span>
                  </div>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Client</span>
                  {client ? (
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "h-6 w-6 rounded-md flex items-center justify-center text-white text-[9px] font-bold",
                          client.color
                        )}
                      >
                        {client.initials}
                      </div>
                      <span className="text-sm font-medium">{client.name}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Scheduled
                  </span>
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {format(post.scheduledAt, "MMM d, yyyy 'at' h:mm a")}
                    </span>
                  </div>
                </div>

              </CardContent>
            </Card>

            {/* Approval Status */}
            {approval && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Client Approval</CardTitle>
                    {approvalStatus && (
                      <span
                        className={cn(
                          "text-[11px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1",
                          approvalStatus.bg,
                          approvalStatus.color
                        )}
                      >
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            approvalStatus.dot
                          )}
                        />
                        {approvalStatus.label}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-[9px] bg-secondary text-secondary-foreground font-semibold">
                        {approval.clientInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{approval.client}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {approval.clientEmail}
                      </p>
                    </div>
                  </div>

                  {approval.note && (
                    <div
                      className={cn(
                        "rounded-lg border-l-4 p-3 text-sm",
                        approval.status === "approved" &&
                          "border-l-green-500 bg-green-50/50",
                        approval.status === "rejected" &&
                          "border-l-red-500 bg-red-50/50",
                        approval.status === "changes_requested" &&
                          "border-l-orange-500 bg-orange-50/50",
                        approval.status === "pending" &&
                          "border-l-amber-400 bg-amber-50/50"
                      )}
                    >
                      {approval.note}
                    </div>
                  )}

                  {approval.status === "pending" && (
                    <Button variant="outline" size="sm" className="w-full text-xs">
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      Send Reminder
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Version History */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Version History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {mockVersions
                    .slice()
                    .reverse()
                    .map((v, i) => (
                      <div key={v.version} className="flex gap-3 pb-4 last:pb-0">
                        <div className="flex flex-col items-center">
                          <div
                            className={cn(
                              "h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
                              i === 0
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground"
                            )}
                          >
                            {v.version}
                          </div>
                          {i < mockVersions.length - 1 && (
                            <div className="w-px flex-1 bg-border mt-1" />
                          )}
                        </div>
                        <div className="pt-0.5">
                          <p className="text-sm font-medium">{v.note}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {v.author} &middot; {v.date}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
            <DialogDescription>
              Update the content, platform, status, and schedule for this post.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Content */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Content</Label>
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[140px] text-sm resize-none"
                placeholder="Write your post content..."
              />
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground">
                  {editContent.length} characters
                </p>
                {editPlatform === "TWITTER" && (
                  <p
                    className={cn(
                      "text-[11px] font-medium",
                      editContent.length > 280
                        ? "text-destructive"
                        : "text-muted-foreground"
                    )}
                  >
                    {280 - editContent.length} remaining
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Platform */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Platform</Label>
                <select
                  value={editPlatform}
                  onChange={(e) => setEditPlatform(e.target.value)}
                  className="flex h-9 w-full rounded-lg border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="INSTAGRAM">Instagram</option>
                  <option value="FACEBOOK">Facebook</option>
                  <option value="TWITTER">Twitter / X</option>
                  <option value="LINKEDIN">LinkedIn</option>
                  <option value="TIKTOK">TikTok</option>
                </select>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Status</Label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="flex h-9 w-full rounded-lg border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="PENDING_APPROVAL">Pending Approval</option>
                  <option value="APPROVED">Approved</option>
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="PUBLISHED">Published</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Date */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Scheduled Date</Label>
                <Input
                  type="date"
                  value={editScheduledDate}
                  onChange={(e) => setEditScheduledDate(e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Time */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Scheduled Time</Label>
                <Input
                  type="time"
                  value={editScheduledTime}
                  onChange={(e) => setEditScheduledTime(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>

            {/* Media placeholder */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Media</Label>
              <div className="rounded-lg border-2 border-dashed p-6 text-center hover:bg-muted/30 transition-colors cursor-pointer">
                <ArrowUpRight className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm font-medium text-muted-foreground">
                  Drag & drop files or click to upload
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  PNG, JPG, GIF, MP4 up to 50MB
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!editContent.trim()}
              className="shadow-sm shadow-primary/25"
            >
              {saved ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  Saved!
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
